import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2 } from 'lucide-react';
import { loadGoogleMaps, HAS_GOOGLE_MAPS } from '../lib/googleMaps';

/**
 * Google Places Autocomplete restricted to the island of Ireland (Republic of
 * Ireland + Northern Ireland). Free text is rejected — users must pick a
 * suggestion before lat/lng are emitted.
 *
 * Props:
 *   value:       current text value
 *   onChange:    (text) => void
 *   onSelect:    ({ place_name, lat, lng, county, eircode }) => void
 *   placeholder, required, disabled, dataTestId, className, id
 */
export const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = 'Search Irish address',
  required = false,
  disabled = false,
  dataTestId,
  className = '',
  id,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [ready, setReady] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const lastQueryRef = useRef('');
  const sessionTokenRef = useRef(null);
  const autocompleteSvcRef = useRef(null);
  const placesSvcRef = useRef(null);

  // Load Google Maps once, build the autocomplete service.
  useEffect(() => {
    let alive = true;
    if (!HAS_GOOGLE_MAPS) return undefined;
    loadGoogleMaps()
      .then(() => {
        if (!alive) return;
        // eslint-disable-next-line no-undef
        autocompleteSvcRef.current = new google.maps.places.AutocompleteService();
        // PlacesService needs a node — invisible div is fine.
        const node = document.createElement('div');
        // eslint-disable-next-line no-undef
        placesSvcRef.current = new google.maps.places.PlacesService(node);
        // eslint-disable-next-line no-undef
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
        setReady(true);
      })
      .catch(() => { /* surfaced as "search disabled" UX below */ });
    return () => { alive = false; };
  }, []);

  const fetchSuggestions = useCallback((q) => {
    if (!q || q.trim().length < 3 || !autocompleteSvcRef.current) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    lastQueryRef.current = q;
    setLoading(true);
    autocompleteSvcRef.current.getPlacePredictions(
      {
        input: q,
        sessionToken: sessionTokenRef.current,
        // Restrict to Ireland (ie) + UK (gb). UK is needed because Northern
        // Ireland is part of the United Kingdom under Google's component
        // restrictions; we filter to NI-only on the result side.
        componentRestrictions: { country: ['ie', 'gb'] },
        types: ['address'],
      },
      (preds, status) => {
        // Race-guard: only apply if query hasn't moved on
        if (lastQueryRef.current !== q) return;
        setLoading(false);
        // eslint-disable-next-line no-undef
        if (status !== google.maps.places.PlacesServiceStatus.OK || !preds) {
          setSuggestions([]);
          setOpen(true);
          setActiveIdx(-1);
          return;
        }
        // Filter GB results to Northern Ireland only (description usually
        // contains "Northern Ireland" or "BT" postcode prefix).
        const filtered = preds.filter((p) => {
          const desc = (p.description || '').toLowerCase();
          if (desc.includes('ireland') && !desc.includes('northern ireland')) return true; // RoI
          if (desc.includes('northern ireland')) return true;
          // BT-prefixed postcode is NI
          if (/\bbt\d/i.test(p.description || '')) return true;
          return false;
        }).slice(0, 5);
        setSuggestions(filtered);
        setOpen(true);
        setActiveIdx(-1);
      }
    );
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange?.(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 300);
  };

  const extractCountyEircode = (place) => {
    let county = '';
    let eircode = '';
    let area = '';
    const parts = place.address_components || [];
    for (const c of parts) {
      const types = c.types || [];
      if (types.includes('administrative_area_level_1') || types.includes('administrative_area_level_2')) {
        if (!county) county = (c.long_name || '').replace(/^County\s+/i, '').trim();
      }
      if (types.includes('postal_code')) {
        eircode = (c.long_name || '').toUpperCase().trim();
      }
      // Town / general area shown to drivers before a booking is confirmed.
      if (!area && (types.includes('locality') || types.includes('postal_town'))) {
        area = (c.long_name || '').trim();
      }
      if (!area && (types.includes('sublocality') || types.includes('sublocality_level_1'))) {
        area = (c.long_name || '').trim();
      }
    }
    return { county, eircode, area };
  };

  const pick = (pred) => {
    if (!placesSvcRef.current) return;
    placesSvcRef.current.getDetails(
      {
        placeId: pred.place_id,
        sessionToken: sessionTokenRef.current,
        fields: ['formatted_address', 'geometry', 'address_components', 'name'],
      },
      (place, status) => {
        // eslint-disable-next-line no-undef
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place) return;
        const lat = place.geometry?.location?.lat?.();
        const lng = place.geometry?.location?.lng?.();
        const placeName = place.formatted_address || pred.description || '';
        const { county, eircode, area } = extractCountyEircode(place);
        onChange?.(placeName);
        onSelect?.({ place_name: placeName, lat, lng, county, eircode, area });
        setOpen(false);
        setSuggestions([]);
        // New session for the next selection.
        // eslint-disable-next-line no-undef
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
      }
    );
  };

  const handleKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && activeIdx < suggestions.length) {
        e.preventDefault();
        pick(suggestions[activeIdx]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Close on click-outside.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={wrapperRef} className="relative" data-testid={dataTestId ? `${dataTestId}-wrapper` : undefined}>
      <Input
        id={id}
        value={value || ''}
        onChange={handleChange}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onKeyDown={handleKey}
        placeholder={!ready && HAS_GOOGLE_MAPS ? 'Loading address search…' : placeholder}
        required={required}
        disabled={disabled || (!ready && HAS_GOOGLE_MAPS)}
        autoComplete="off"
        className={className}
        data-testid={dataTestId}
      />
      {loading && (
        <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
      )}
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-sm shadow-lg max-h-72 overflow-y-auto"
          data-testid={dataTestId ? `${dataTestId}-suggestions` : undefined}
        >
          {suggestions.map((s, i) => {
            const isActive = i === activeIdx;
            const main = s.structured_formatting?.main_text || s.description;
            const secondary = s.structured_formatting?.secondary_text || '';
            return (
              <li
                key={s.place_id}
                className={`px-3 py-2 text-sm cursor-pointer flex items-start gap-2 ${isActive ? 'bg-[#2E7D32]/10' : 'hover:bg-gray-50'}`}
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                onMouseEnter={() => setActiveIdx(i)}
                data-testid={dataTestId ? `${dataTestId}-option-${i}` : undefined}
              >
                <MapPin className="w-4 h-4 text-[#2E7D32] flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{main}</p>
                  {secondary && <p className="text-xs text-gray-500 truncate">{secondary}</p>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
